import { Component, OnInit, inject, OnDestroy } from '@angular/core';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { FormBuilder, FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import { MaterialModule } from '@app/material.module';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@services/api/auth/auth.service';
import { SharedDataService } from '@services/share-data/share-data.service';
import { emailFormatValidator } from '@core/shared/form-validators';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TokenStorageService } from '@services/local-storage/token-storage/token-storage.service';
import { UserStorageService } from '@services/local-storage/user-storage/user-storage.service';
import { environment } from '@environment/environment';

@Component({
  selector: 'app-login',
  imports: [RouterModule, MaterialModule, ReactiveFormsModule, CommonModule, TranslateModule, TablerIconsModule],
  standalone: true,
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent {
  loginForm: FormGroup;
  hidePassword = true;
  private destroy$ = new Subject<void>();
  private router = inject(Router);
  private auth_service = inject(AuthService);
  private share_data_service = inject(SharedDataService);
  private token_storage_service = inject(TokenStorageService);
  private user_storage_service = inject(UserStorageService);

  constructor(private fb: FormBuilder) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, emailFormatValidator]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.auth_service
        .login(
          this.loginForm.get('email')?.value,
          this.loginForm.get('password')?.value
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async (response) => {
            // Con l'OTP disattivato lato backend (LOGIN_OTP_DISABLED, solo test/demo)
            // il login restituisce gia' il token: si entra senza la pagina OTP.
            const token = (response?.data as { token?: string } | null)?.token;
            if (token) {
              await this.token_storage_service.setTokenStorage(token);
              const userData = await firstValueFrom(this.auth_service.get_user_data());
              await this.user_storage_service.setUserStorage(userData.data);
              this.router.navigate([environment.home_page]);
              return;
            }
            const email = this.loginForm.get('email')?.value;
            this.share_data_service.setData(email);
            this.router.navigate(['/verify-otp']);
          },
          error: (error) => {
            this.loginForm.reset();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get emailFormControl() {
    return this.loginForm.get('email');
  }

  get passwordFormControl() {
    return this.loginForm.get('password');
  }

}
